/**
 * 🔒 VAULT-SEAL-ADJACENT — DO NOT MODIFY (Feb 24 2026). See /app/memory/VAULT_SEAL.md.
 * Sealed until the user types "Upgrade Vault".
 *
 * A big "Request Secret Vault" CTA that:
 *   - hides itself if the user already has access
 *   - opens VaultRequestModal on click
 *   - exposes a `variant` prop so we can render it as either a profile-page
 *     hero card or a slim library banner.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ShieldCheck, CreditCard } from 'lucide-react';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';
import VaultRequestModal from './VaultRequestModal';
import BuyVaultAccessModal from './BuyVaultAccessModal';

const RequestVaultButton = ({ variant = 'profile' }) => {
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [user, setUser] = useState(null);
  const [pendingRequest, setPendingRequest] = useState(false);
  const [open, setOpen] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await authFetch(`${BACKEND_URL}/api/auth/me`);
        if (me.ok && !cancelled) {
          const data = await me.json();
          setUser(data);
          // Probe the user's pending request, if any.
          if (!data.vault_access_granted) {
            const req = await authFetch(`${BACKEND_URL}/api/vault/request-status`).catch(() => null);
            // Endpoint may not exist; fail silently.
            if (req && req.ok) {
              const rd = await req.json();
              if (!cancelled) setPendingRequest(!!rd.pending);
            }
          }
        }
      } catch {
        /* noop */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [BACKEND_URL]);

  if (!user || user.role === 'admin') return null; // admins don't need the button
  // Already granted → show a green confirmation chip.
  if (user.vault_access_granted) {
    return (
      <div
        className={
          variant === 'banner'
            ? 'relative overflow-hidden flex items-center justify-between gap-3 p-3 rounded-xl border border-emerald-500/50 bg-[length:200%_200%] bg-gradient-to-r from-emerald-500/40 via-teal-400/30 to-emerald-600/40 animate-[vault-shimmer_6s_ease-in-out_infinite]'
            : 'relative overflow-hidden flex items-center justify-between gap-3 p-4 rounded-2xl border border-emerald-500/40 bg-[length:200%_200%] bg-gradient-to-r from-emerald-500/30 via-teal-400/25 to-emerald-600/30 animate-[vault-shimmer_6s_ease-in-out_infinite]'
        }
        data-testid="vault-granted-chip"
      >
        <style>{`@keyframes vault-shimmer { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }`}</style>
        <div className="flex items-center gap-2 text-emerald-100">
          <ShieldCheck className="w-4 h-4 drop-shadow" />
          <span className="text-sm font-semibold">Vault access granted</span>
          {user.id_verified && (
            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-blue-600/30 text-blue-100 border border-blue-300/40 ml-1">
              ID Verified
            </span>
          )}
        </div>
        <Button
          size="sm"
          onClick={() => navigate('/vault')}
          className="bg-emerald-600 hover:bg-emerald-700 text-white relative z-10"
          data-testid="open-vault-btn"
        >
          Open Vault
        </Button>
      </div>
    );
  }

  // Banner variant — slim, library-friendly.
  if (variant === 'banner') {
    return (
      <>
        <div
          className="flex items-center justify-between gap-3 p-3 mb-4 rounded-xl bg-gradient-to-r from-fuchsia-700/40 via-purple-800/30 to-indigo-900/40 border border-fuchsia-500/40"
          data-testid="vault-request-banner"
        >          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 shrink-0 rounded-lg bg-fuchsia-600/30 border border-fuchsia-400/40 flex items-center justify-center">
              <Lock className="w-4 h-4 text-fuchsia-200" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate">
                Unlock the Secret Vault
              </p>
              <p className="text-[11px] text-fuchsia-200/80 truncate">
                Private 18+ catalogue · admin-granted access only
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => setOpen(true)}
            className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white shrink-0"
            data-testid="vault-request-banner-btn"
          >
            {pendingRequest ? 'Request pending' : 'Request access'}
          </Button>
          <Button
            size="sm"
            onClick={() => setBuyOpen(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
            data-testid="vault-buy-banner-btn"
          >
            <CreditCard className="w-3.5 h-3.5 mr-1" />
            Buy £20
          </Button>
        </div>

        {open && (
          <VaultRequestModal
            onClose={() => setOpen(false)}
            onSubmitted={() => setPendingRequest(true)}
          />
        )}
        {buyOpen && (
          <BuyVaultAccessModal onClose={() => setBuyOpen(false)} />
        )}
      </>
    );
  }

  // Profile / hero variant — large card.
  return (
    <>
      <div
        className="rounded-2xl p-6 bg-gradient-to-br from-fuchsia-700/40 via-purple-800/30 to-indigo-900/40 border border-fuchsia-500/40 shadow-[0_0_60px_-25px_rgba(217,70,239,0.55)]"
        data-testid="vault-request-card"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-fuchsia-600/30 border border-fuchsia-400/40 flex items-center justify-center">
            <Lock className="w-6 h-6 text-fuchsia-200" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Request Secret Vault</h3>
            <p className="text-xs text-fuchsia-200/80">
              Private 18+ catalogue · invite-only access
            </p>
          </div>
        </div>
        <p className="text-sm text-gray-300 leading-relaxed mb-4">
          The vault contains private books, audiobooks, and audio series reserved for verified
          adult members. Submit your date of birth and a short note — an admin will review your
          request and issue a personal access code.
        </p>
        <Button
          onClick={() => setOpen(true)}
          className="w-full py-6 text-base font-semibold bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
          data-testid="vault-request-large-btn"
        >
          <Lock className="w-5 h-5 mr-2" />
          {pendingRequest ? 'Request pending — awaiting admin' : 'Request access to the Secret Vault'}
        </Button>
        <div className="mt-3 pt-3 border-t border-fuchsia-500/20">
          <p className="text-[11px] text-gray-400 mb-2 text-center">
            Or skip the queue — buy access for £20:
          </p>
          <Button
            onClick={() => setBuyOpen(true)}
            variant="outline"
            className="w-full bg-emerald-600/20 hover:bg-emerald-600/40 border-emerald-500/40 text-emerald-100"
            data-testid="vault-buy-large-btn"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Buy Vault Access · £20
          </Button>
        </div>
        <p className="text-[11px] text-gray-500 mt-3 text-center">
          By requesting access you confirm you are 18 or older.
        </p>
      </div>

      {open && (
        <VaultRequestModal
          onClose={() => setOpen(false)}
          onSubmitted={() => setPendingRequest(true)}
        />
      )}
      {buyOpen && (
        <BuyVaultAccessModal onClose={() => setBuyOpen(false)} />
      )}
    </>
  );
};

export default RequestVaultButton;
